import { useState } from 'react';
import { Button } from '../components/Button';
import styles from './ShareScore.module.css';

interface ShareScoreProps {
  score: number;
  /** Where the recipient should land — the game itself, ideally. */
  url: string;
  /** Named games get a mention, so the link reads as an invitation. */
  gameTitle?: string;
}

function buildMessage(score: number, gameTitle?: string): string {
  const where = gameTitle ? `“${gameTitle}” on MergeBalls` : 'MergeBalls';
  return `I scored ${score.toLocaleString()} on ${where} 🎈 Think you can beat that?`;
}

/**
 * One tap to share a score, with the message shown rather than hidden behind the
 * button — people are reluctant to fire off something they haven't read.
 *
 * On a phone this opens the native share sheet with the text and link already written,
 * which is the whole point: anything that makes someone compose a message themselves
 * is where sharing stops happening. Everywhere else it copies the same thing.
 */
export function ShareScore({ score, url, gameTitle }: ShareScoreProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const message = buildMessage(score, gameTitle);
  const prettyUrl = url.replace(/^https?:\/\//, '');

  async function share() {
    if (navigator.share) {
      try {
        // `url` stays a separate field: share targets that understand it render a
        // proper link preview rather than a bare URL glued onto the text.
        await navigator.share({ title: 'MergeBalls', text: message, url });
        return;
      } catch {
        /* dismissed or unsupported — fall through to copying */
      }
    }

    try {
      await navigator.clipboard.writeText(`${message} ${url}`);
      setState('copied');
      setTimeout(() => setState('idle'), 2200);
    } catch {
      setState('failed');
      setTimeout(() => setState('idle'), 2600);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.bubble}>
        <p className={styles.text}>{message}</p>
        <p className={styles.link}>{prettyUrl}</p>
      </div>
      <Button variant="secondary" block onClick={() => void share()} className={styles.button}>
        {state === 'copied' ? '✓ Copied — go paste it!' : '🎈 Share this'}
      </Button>
      {state === 'failed' ? (
        <p className={styles.note}>Couldn’t copy — select the text above instead.</p>
      ) : null}
    </div>
  );
}
